import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import axios from "axios";

const AuthContext = createContext(null);
const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const REFRESH_COOLDOWN_MS = 60 * 1000;
const REFRESH_LOCK_KEY = "jfm-auth-refresh-lock";
const REFRESH_LAST_KEY = "jfm-auth-last-refresh";
const REFRESH_LOCK_TTL_MS = 15 * 1000;

function refreshLockId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);
  const lastRefreshRef = useRef(Date.now());
  const refreshRequestRef = useRef(null);
  const refreshLockIdRef = useRef(refreshLockId());

  const loadSession = async () => {
    try {
      const response = await axios.get(`${AUTH_API}/me`);
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${AUTH_API}/login`, { email, password });
    flushSync(() => {
      setUser(response.data);
    });
    return response.data;
  };

  const clearSession = useCallback(() => {
    flushSync(() => {
      setUser(null);
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${AUTH_API}/logout`);
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refreshActiveSession = useCallback(async () => {
    const now = Date.now();
    const sharedLastRefresh = Number(
      window.localStorage.getItem(REFRESH_LAST_KEY) || "0",
    );
    if (
      now - Math.max(lastRefreshRef.current, sharedLastRefresh) <
      REFRESH_COOLDOWN_MS
    ) {
      return;
    }
    if (refreshRequestRef.current) {
      return refreshRequestRef.current;
    }

    const runRefresh = async () => {
      const refreshedWhileWaiting = Number(
        window.localStorage.getItem(REFRESH_LAST_KEY) || "0",
      );
      if (Date.now() - refreshedWhileWaiting < REFRESH_COOLDOWN_MS) {
        lastRefreshRef.current = refreshedWhileWaiting;
        return;
      }
      try {
        const response = await axios.post(`${AUTH_API}/refresh`);
        const refreshedAt = Date.now();
        window.localStorage.setItem(REFRESH_LAST_KEY, String(refreshedAt));
        lastRefreshRef.current = refreshedAt;
        setUser(response.data);
      } catch (error) {
        if ([401, 403].includes(error.response?.status)) {
          setUser(null);
        }
      }
    };

    const runWithLocalFallbackLock = async () => {
      const lockOwner = refreshLockIdRef.current;
      let existingLock = null;
      try {
        existingLock = JSON.parse(window.localStorage.getItem(REFRESH_LOCK_KEY) || "null");
      } catch {
        window.localStorage.removeItem(REFRESH_LOCK_KEY);
      }
      if (existingLock?.expiresAt > now && existingLock.owner !== lockOwner) {
        lastRefreshRef.current = now;
        return;
      }
      window.localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ owner: lockOwner, expiresAt: now + REFRESH_LOCK_TTL_MS }),
      );
      try {
        await runRefresh();
      } finally {
        const activeLock = JSON.parse(window.localStorage.getItem(REFRESH_LOCK_KEY) || "null");
        if (activeLock?.owner === lockOwner) {
          window.localStorage.removeItem(REFRESH_LOCK_KEY);
        }
      }
    };

    const refreshRequest = navigator.locks?.request
      ? navigator.locks.request(REFRESH_LOCK_KEY, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            lastRefreshRef.current = Date.now();
            return;
          }
          await runRefresh();
        })
      : runWithLocalFallbackLock();
    refreshRequestRef.current = refreshRequest;
    try {
      return await refreshRequest;
    } finally {
      refreshRequestRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      window.clearTimeout(idleTimerRef.current);
      return undefined;
    }

    const signOutForInactivity = () => {
      logout();
    };
    const resetIdleTimer = () => {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(signOutForInactivity, IDLE_TIMEOUT_MS);
      refreshActiveSession();
    };
    const activityEvents = ["pointerdown", "keydown", "touchstart"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer));
    resetIdleTimer();
    return () => {
      window.clearTimeout(idleTimerRef.current);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
  }, [logout, refreshActiveSession, user]);

  const value = {
    user,
    loading,
    login,
    logout,
    clearSession,
    refresh: loadSession,
    hasPermission: (permission) =>
      Boolean(user?.permissions?.includes(permission)),
    isFeatureEnabled: (feature) => {
      if (!user) return false;
      if (user.platform_role === "platform_owner") return true;
      const flag = user.features?.[feature];
      return flag === undefined ? true : Boolean(flag);
    },
    canAccessBilling: Boolean(user?.permissions?.includes("billing:read")),
    canManageProjectWorkflow: Boolean(
      user?.permissions?.includes("project:workflow"),
    ),
    canManageActiveJobs: Boolean(
      user?.permissions?.includes("active_jobs:manage"),
    ),
    canManageRecords: Boolean(user?.permissions?.includes("records:manage")),
    canManageCommunications: Boolean(
      user?.permissions?.includes("communications:manage"),
    ),
    canCreateFieldNotes: Boolean(
      user?.permissions?.includes("communications:manage") ||
        user?.permissions?.includes("field_notes:create"),
    ),
    canManageUsers: Boolean(user?.permissions?.includes("users:manage")),
    canManagePayroll: Boolean(user?.permissions?.includes("payroll:manage")),
    canAccessAnalytics: Boolean(user?.permissions?.includes("analytics:read")),
    canManageAssets: Boolean(user?.permissions?.includes("assets:manage")),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
